import { MANDALA_GATE_SEQUENCE_CCW } from '../mandala.constants';
import {
  HdBodygraphCenter,
  HdBodygraphConfig,
  HdBodygraphPlanetaryActivation,
  HdBodygraphRenderState,
} from '../../bodygraph/hd-bodygraph.models';
import { HD_BODYGRAPH_CHANNELS } from '../../bodygraph/hd-bodygraph-metadata';
import { hdGatesCountingForChannel, hdPreShiftGates } from '../../hd-global-shift';
import { getDefinedClusters } from '../../bodygraph/basic-properties/hd-chart-properties.util';
import {
  getHdChannelManifestationType,
  HdChannelManifestationType,
} from '../../bodygraph/hd-channel-manifestation-type';
import { resolveHdPlanetLineFixing } from '../../bodygraph/hd-line-fixing.utils';
import {
  getHdPlanetDisplayConfigByPlanetId,
  HdPlanetKey,
} from '../../bodygraph/planet-grid/hd-planet-display-config';

/**
 * Maan-transit-berekening (spec `2026-07-06-hd-moon-transit-chart-design.md`
 * §3): pure functies die per wielpoort bepalen of een maan-activatie een
 * kanaal zou completeren met een bestaande activatie van de geladen chart.
 * Volledig client-side afleidbaar — geen live planeetdata nodig.
 */

/** Taalonafhankelijke exalt/det-planet-keys van één I-Tjing-lijn van een poort. */
export interface HdMoonTransitLineDuality {
  readonly exaltationPlanet: string;
  readonly detrimentPlanet: string;
}

/**
 * Lookup van lijndata per (poort, lijn). Als parameter geïnjecteerd zodat de
 * berekening puur en service-vrij testbaar blijft; de page bindt dit aan
 * `HdGateInfoService.gateInfoIn(...).translations.lines[line - 1]`.
 */
export type HdMoonTransitLineDataLookup = (gate: number, line: number) => HdMoonTransitLineDuality | null;

/** Eén maan-fixatie op een bestaande activatie van de partnerpoort. */
export interface HdMoonTransitFixation {
  readonly side: 'personality' | 'design';
  /** Planeet van de gefixeerde (bestaande) activatie, voor de glyph-weergave. */
  readonly planetKey: HdPlanetKey | null;
  readonly planetId: number;
  readonly line: number;
  readonly kind: 'exaltation' | 'detriment';
}

/** Eén kanaal dat de maan tijdelijk zou completeren ("formatie"). */
export interface HdMoonTransitFormation {
  readonly moonGate: number;
  readonly partnerGate: number;
  /** Canonieke kanaal-key uit `HD_BODYGRAPH_CHANNELS` (bv. `'30-41'`). */
  readonly channelKey: string;
  readonly manifestationType: HdChannelManifestationType;
  /** Centers van het kanaal die in de basischart nog niet defined waren (0–2). */
  readonly newlyDefinedCenters: readonly HdBodygraphCenter[];
  /** True wanneer het kanaal twee losse defined-eilanden van de basischart verbindt (§3.6). */
  readonly bridgesSplit: boolean;
  readonly fixations: readonly HdMoonTransitFixation[];
}

export interface HdMoonTransitOverview {
  /** Enkel poorten mét formaties hebben een entry. */
  readonly formationsByGate: ReadonlyMap<number, readonly HdMoonTransitFormation[]>;
  /** Vlakke lijst in wielvolgorde (start 13) — voor tests, legende en de latere kalender. */
  readonly formations: readonly HdMoonTransitFormation[];
}

export interface HdMoonTransitComputationInput {
  readonly configuration: HdBodygraphConfig;
  readonly planetaryActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly renderState: HdBodygraphRenderState;
  readonly lineData: HdMoonTransitLineDataLookup;
}

/** Wielvolgorde CCW met poort 13 (Q1-start) vooraan — spec §3.1. */
export const HD_MOON_TRANSIT_GATE_ORDER: readonly number[] = (() => {
  const startIndex = MANDALA_GATE_SEQUENCE_CCW.indexOf(13);
  return [...MANDALA_GATE_SEQUENCE_CCW.slice(startIndex), ...MANDALA_GATE_SEQUENCE_CCW.slice(0, startIndex)];
})();

/**
 * Bepaalt per wielpoort welke kanalen een maan-activatie zou completeren.
 * Poorten die de chart zelf al activeert worden overgeslagen (v1: "minimale
 * impact" is out of scope, incl. poorten van al volledig defined kanalen).
 */
export function buildHdMoonTransitOverview(input: HdMoonTransitComputationInput): HdMoonTransitOverview {
  const activeGates = new Set(input.configuration.activations.map(activation => activation.gate));
  // Kanaalbreuk na de shift (19/49): een partnerpoort van een post-2027-geboorte
  // kan dat kanaal niet vervolledigen (zie hd-global-shift.ts).
  const preShiftGates = hdPreShiftGates(input.configuration.activations);
  const clusterIndexByCenter = buildClusterIndex(input.renderState);

  const formationsByGate = new Map<number, HdMoonTransitFormation[]>();
  const formations: HdMoonTransitFormation[] = [];

  for (const moonGate of HD_MOON_TRANSIT_GATE_ORDER) {
    if (activeGates.has(moonGate)) {
      continue;
    }
    for (const channel of HD_BODYGRAPH_CHANNELS) {
      const [gateA, gateB] = channel.gates;
      if (gateA !== moonGate && gateB !== moonGate) {
        continue;
      }
      const partnerGate = gateA === moonGate ? gateB : gateA;
      if (!hdGatesCountingForChannel(channel.key, activeGates, preShiftGates).has(partnerGate)) {
        continue;
      }

      const newlyDefinedCenters = channel.centers.filter(center => !clusterIndexByCenter.has(center));
      const [centerA, centerB] = channel.centers;
      const clusterA = clusterIndexByCenter.get(centerA);
      const clusterB = clusterIndexByCenter.get(centerB);
      const bridgesSplit = clusterA != null && clusterB != null && clusterA !== clusterB;

      const formation: HdMoonTransitFormation = {
        moonGate,
        partnerGate,
        channelKey: channel.key,
        // Non-null: de key komt rechtstreeks uit HD_BODYGRAPH_CHANNELS.
        manifestationType: getHdChannelManifestationType(channel.key) ?? 'projected',
        newlyDefinedCenters,
        bridgesSplit,
        fixations: buildMoonFixations(partnerGate, input.planetaryActivations, input.lineData),
      };

      formations.push(formation);
      const existing = formationsByGate.get(moonGate);
      if (existing) {
        existing.push(formation);
      } else {
        formationsByGate.set(moonGate, [formation]);
      }
    }
  }

  return { formationsByGate, formations };
}

/** Center → index van zijn defined-eiland (ongedefinieerde centers ontbreken). */
function buildClusterIndex(renderState: HdBodygraphRenderState): ReadonlyMap<HdBodygraphCenter, number> {
  const index = new Map<HdBodygraphCenter, number>();
  getDefinedClusters(renderState).forEach((cluster, clusterIndex) => {
    for (const center of cluster) {
      index.set(center, clusterIndex);
    }
  });
  return index;
}

/**
 * Maan-fixaties op de bestaande activaties van de partnerpoort: per
 * planetaire activatie (die een lijn heeft) checken of de máán de
 * exaltation-/detriment-planeet van die lijn is. `both` levert twee entries
 * op (exaltation + detriment), zoals het bestaande stacked-badge-geval.
 */
function buildMoonFixations(
  partnerGate: number,
  planetaryActivations: readonly HdBodygraphPlanetaryActivation[],
  lineData: HdMoonTransitLineDataLookup,
): HdMoonTransitFixation[] {
  const fixations: HdMoonTransitFixation[] = [];
  for (const activation of planetaryActivations) {
    if (activation.gate !== partnerGate) {
      continue;
    }
    if (activation.side !== 'personality' && activation.side !== 'design') {
      continue;
    }
    const line = lineData(partnerGate, activation.line);
    if (line == null) {
      continue;
    }
    const fixing = resolveHdPlanetLineFixing('moon', line);
    if (fixing === 'none') {
      continue;
    }
    const base = {
      side: activation.side,
      planetKey: getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.key ?? null,
      planetId: activation.planetId,
      line: activation.line,
    };
    if (fixing === 'exaltation' || fixing === 'both') {
      fixations.push({ ...base, kind: 'exaltation' });
    }
    if (fixing === 'detriment' || fixing === 'both') {
      fixations.push({ ...base, kind: 'detriment' });
    }
  }
  return fixations;
}
